import { FC, useEffect, useState } from "react";
import { Room } from "../../../models/Room";
import { RoomToken } from "../../../models/RoomToken";
import { Fire } from "../../../services/FireClient";
import { My } from "../../../services/My";
import { SectionCard } from "./SectionCard";
import { TokenCreateModal } from "./TokenCreateModal";
import { Trans, t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { Button } from "../../../components/ui/button";
import { Plus, Zap, ZapOff, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog";
import { useToast } from "../../../components/ui/use-toast";
import { cn } from "../../../lib/utils";

/** 스코프 키를 짧은 라벨로 변환 */
function scopeLabel(scope: string): string {
  switch (scope) {
    case 'message:send': return t`Messages`;
    case 'files:read': return t`Files`;
    case 'memos:read': return t`Memos`;
    default: return scope;
  }
}

export const PanelListTokens: FC<{ room: Room }> = ({ room }) => {
  useLingui();
  const [tokens, setTokens] = useState<RoomToken[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editToken, setEditToken] = useState<RoomToken | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<RoomToken | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { toast } = useToast();
  const isCreator = Room.isCreator(room, My.user.id);

  useEffect(() => {
    const unsub = Fire.token.subscribeToRoomTokens(room.id, (list) => {
      setTokens(list);
    });
    return unsub;
  }, [room.id]);

  async function handleDelete() {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      const result = await Fire.token.deleteToken(room.id, deleteTarget.id);
      toast({
        title: t`Token deleted`,
        description: t`${result.messagesDeleted} messages removed`,
      });
    } catch (e) {
      toast({ title: t`Failed to delete token`, duration: 2000 });
    } finally {
      setIsDeleting(false);
      setDeleteTarget(null);
    }
  }

  if (tokens.length === 0 && !isCreator) return null;

  return (
    <>
      <SectionCard
        title={t`API Tokens`}
        count={tokens.length}
        collapsible={true}
        defaultExpanded={true}
        actions={
          isCreator ? (
            <Button variant="icon-panel" onClick={() => setShowCreate(true)} title={t`Add token`}>
              <Plus className="w-4 h-4" />
            </Button>
          ) : undefined
        }
      >
        {tokens.length === 0 ? (
          <p className="text-body font-medium text-muted-foreground py-3 text-center">
            <Trans>No API tokens</Trans>
          </p>
        ) : (
          <div className="flex flex-col -mx-2">
            {tokens.map((token) => {
              const scopes = token.scopes ?? ['message:send'];
              return (
                <div
                  key={token.id}
                  className={cn(
                    "flex items-center gap-3 py-2 px-2 rounded-lg transition-colors",
                    isCreator ? "hover:bg-accent cursor-pointer" : "cursor-default"
                  )}
                  onClick={() => isCreator && setEditToken(token)}
                >
                  {/* 아이콘 */}
                  <div className={cn(
                    "shrink-0 w-7 h-7 rounded-md flex items-center justify-center",
                    token.isActive
                      ? "bg-[color:rgb(var(--badge-api-bg))]/15"
                      : "bg-muted"
                  )}>
                    {token.isActive
                      ? <Zap className="w-3.5 h-3.5 text-[color:rgb(var(--badge-api-bg))]" />
                      : <ZapOff className="w-3.5 h-3.5 text-muted-foreground" />
                    }
                  </div>

                  {/* 정보 */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="font-medium text-sm truncate text-[color:rgb(var(--text-body))]">{token.name}</span>
                      {token.isActive ? (
                        (token.currentMonthUsage ?? 0) >= (token.monthlyQuota ?? 10000) ? (
                          <span className="text-meta-sm font-semibold text-destructive bg-destructive/10 px-1.5 py-0.5 rounded leading-none whitespace-nowrap">
                            <Trans>Limited</Trans>
                          </span>
                        ) : (
                          <span className="text-meta-sm font-semibold text-[color:rgb(var(--badge-api-bg))] bg-[color:rgb(var(--badge-api-bg))]/10 px-1.5 py-0.5 rounded leading-none whitespace-nowrap">
                            <Trans>Active</Trans>
                          </span>
                        )
                      ) : (
                        <span className="text-meta-sm font-semibold text-muted-foreground bg-muted px-1.5 py-0.5 rounded leading-none whitespace-nowrap">
                          <Trans>Inactive</Trans>
                        </span>
                      )}
                    </div>
                    {/* 권한 태그 + 사용량 */}
                    <div className="flex items-center gap-1 mt-0.5">
                      {scopes.map(s => (
                        <span key={s} className="text-meta-sm text-muted-foreground bg-muted px-1.5 py-px rounded leading-tight">
                          {scopeLabel(s)}
                        </span>
                      ))}
                      <span className="text-meta-sm text-muted-foreground ml-auto tabular-nums">
                        {(token.currentMonthUsage ?? 0).toLocaleString()} / {(token.monthlyQuota ?? 10000).toLocaleString()}
                      </span>
                    </div>
                  </div>

                  {/* 삭제 버튼 (creator만) */}
                  {isCreator && (
                    <Button
                      variant="icon-panel"
                      onClick={(e) => { e.stopPropagation(); setDeleteTarget(token); }}
                      title={t`Delete token`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </SectionCard>

      {/* 토큰 생성 모달 */}
      {showCreate && (
        <TokenCreateModal
          room={room}
          open={showCreate}
          onClose={() => setShowCreate(false)}
        />
      )}

      {/* 토큰 편집 모달 */}
      {editToken && (
        <TokenCreateModal
          room={room}
          open={!!editToken}
          onClose={() => setEditToken(null)}
          editToken={editToken}
        />
      )}

      {/* 삭제 확인 다이얼로그 */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle><Trans>Delete token</Trans> "{deleteTarget?.name}"</AlertDialogTitle>
            <AlertDialogDescription>
              <Trans>This will permanently delete all messages sent via this token and their replies. This action cannot be undone.</Trans>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}><Trans>Cancel</Trans></AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? t`Deleting...` : t`Delete`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
